import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { AlignHorizontal, AlignVertical } from 'core/components/codeMirror/CodeMirror'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Tooltip from 'core/elements/tooltip'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Downshift from 'downshift'
import { isNil } from 'ramda'
import React, { useCallback, useMemo, useRef } from 'react'
import generateTestId from 'utils/test-helpers'
import Text from '../Text'
import DropdownCommonProps, { DropdownItemSpec } from './DropdownCommonProps'
import DropdownInput from './DropdownInput'
import DropdownItem from './DropdownItem'
import DropdownMenu from './DropdownMenu'
import DropdownToggle from './DropdownToggle'
import { defaultAllKey, defaultNoneKey, dropdownDefaultWidth } from './constants'
import { defaultItemToString } from './helpers'
import useStyles from './useStyles'

interface Align {
  vertical: AlignVertical
  horizontal: AlignHorizontal
}
interface Offset {
  vertical: number
  horizontal: number
}
export interface DropdownProps<V, T extends DropdownItemSpec<V> = DropdownItemSpec<V>>
  extends DropdownCommonProps<V, T> {
  initialValue?: V
  value?: V | '__all__' | '__none__'
  onChange?: (selectedValue: V | '__all__' | '__none__') => void
  noBlankValue?: boolean
  showAll?: boolean
  allLabel?: string
  allKey?: string
  showNone?: boolean
  noneLabel?: string
  noneKey?: string
  showClearButton?: boolean
  icon?: string
  iconSize?: string
  id?: string
  info?: string | React.ReactNode
  align?: Align
  offset?: Offset
  onRefresh?: (...args: unknown[]) => void | Promise<void>
}

const blankItem = { key: '', label: '', value: null }

export default function Dropdown<V, T extends DropdownItemSpec<V> = DropdownItemSpec<V>>(
  props: DropdownProps<V, T>,
) {
  const {
    value,
    initialValue,
    itemToString = defaultItemToString,
    onChange,
    items,
    label,
    error,
    name,
    placeholder = 'Select an option',
    enableSearch,
    width = dropdownDefaultWidth,
    noBlankValue,
    className,
    showAll = false,
    allLabel = 'All',
    allKey = defaultAllKey,
    showNone = false,
    noneLabel = 'None',
    noneKey = defaultNoneKey,
    compact,
    showClearButton = enableSearch,
    icon,
    iconSize,
    id,
    info,
    align,
    offset,
    onRefresh,
  } = props
  const showAllItem = useMemo<T>(() => ({ key: allKey, label: allLabel, value: allKey } as T), [
    allKey,
    allLabel,
  ])
  const showNoneItem = useMemo<T>(() => ({ key: noneKey, label: noneLabel, value: noneKey } as T), [
    noneKey,
    noneLabel,
  ])
  const input = useRef(null)
  const classes = useStyles(props)

  const handleChange = useCallback(
    (item: T) => {
      onChange(item?.value)
    },
    [onChange],
  )

  const initialSelectedItem = useMemo<T>(() => {
    if (!isNil(initialValue) && !noBlankValue) {
      return items.find(({ value }) => value === initialValue)
    }
    return noBlankValue && !enableSearch
      ? showAll || showNone
        ? showAll
          ? showAllItem
          : showNoneItem
        : items.length
        ? items[0]
        : (blankItem as T)
      : (blankItem as T)
  }, [])

  const selectedItem = useMemo<T>(() => {
    if (showAll && (isNil(value) || value === allKey)) {
      return showAllItem
    }
    if (showNone && (isNil(value) || value === noneKey)) {
      return showNoneItem
    }
    if (!isNil(value) && !Array.isArray(value)) {
      return items.find(({ value: itemValue }) => itemValue === value) || initialSelectedItem
    }
    return noBlankValue && !enableSearch && items.length ? items[0] : initialSelectedItem
  }, [showAll, showNone, items, value, initialSelectedItem])

  const getFilteredItems = useCallback(
    (inputValue) => {
      const menuItems = []
      if (showAll) {
        menuItems.push(showAllItem)
      }
      if (showNone) {
        menuItems.push(showNoneItem)
      }
      return items.reduce((acc, item) => {
        if (
          enableSearch &&
          inputValue &&
          !item.label.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase())
        ) {
          return acc
        }
        acc.push(item)
        return acc
      }, menuItems)
    },
    [items, showAll, showNone, showAllItem],
  )

  return (
    <>
      <Downshift<T>
        initialSelectedItem={initialSelectedItem}
        selectedItem={onChange ? selectedItem : null}
        onChange={onChange ? handleChange : null}
        itemToString={itemToString}
      >
        {({
          getInputProps,
          getToggleButtonProps,
          getMenuProps,
          selectItem,
          isOpen,
          clearSelection,
          inputValue,
          toggleMenu,
          getLabelProps,
        }) => (
          <div id={id} className={clsx(classes.dropdownWrapper, 'dropdown-container', className)}>
            {label && (
              <Text
                data-testid={generateTestId(label, 'dropdown', 'label')}
                {...getLabelProps()}
                onClick={() => {
                  if (props.disabled) return
                  toggleMenu()
                  !isOpen && input.current?.focus()
                }}
                component="label"
                variant="inputLabel"
                className={clsx(classes.label, 'label')}
              >
                <div className={clsx(classes.wrapper, 'wrapper')}>
                  {label}
                  {info && (
                    <HintTooltip
                      message={info || label || placeholder}
                      align={align}
                      offset={offset}
                      origin={origin}
                      dataTestId={generateTestId(label, 'dropdown', 'hint')}
                    />
                  )}
                </div>
              </Text>
            )}

            <div
              className={clsx(classes.inputFrame, 'inputFrame', {
                [classes.expandedInputFrame]: isOpen,
              })}
              data-testid={generateTestId(id, 'dropdown', 'input')}
              id={`${id}-field`}
              style={{ scrollMarginTop: '40px' }}
            >
              {enableSearch ? (
                <DropdownInput
                  {...getInputProps({
                    ref: input,
                    name,
                    placeholder,
                    onKeyDown(event) {
                      if (event.key === 'Enter') {
                        event.preventDefault() // Prevent default behavior of the Enter key
                      }
                    },
                  })}
                  title={inputValue}
                  onRefresh={onRefresh}
                  toggleMenu={toggleMenu}
                  isOpen={isOpen}
                />
              ) : (
                <div
                  data-testid={generateTestId(id, 'dropdown', 'list')}
                  {...getToggleButtonProps()}
                  className={clsx(classes.placeholder, 'placeholder')}
                  title={inputValue}
                >
                  {icon && (
                    <FontAwesomeIcon className={classes.icon} size={iconSize}>
                      {icon}
                    </FontAwesomeIcon>
                  )}

                  <span className={clsx({ [classes.placeholderText]: !icon })}>
                    {inputValue || placeholder}
                  </span>
                </div>
              )}
              {inputValue && (!noBlankValue || enableSearch) && showClearButton ? (
                <DropdownToggle
                  onClick={() => clearSelection()}
                  aria-label="clear selection"
                  data-testid={generateTestId(id, 'dropdown', 'clear', 'button')}
                >
                  <FontAwesomeIcon solid size="sm">
                    xmark
                  </FontAwesomeIcon>
                </DropdownToggle>
              ) : (
                <DropdownToggle
                  {...getToggleButtonProps()}
                  data-testid={generateTestId(id, 'dropdown', 'toggle', 'button')}
                >
                  <FontAwesomeIcon solid size="sm">
                    {isOpen ? 'caret-up' : 'caret-down'}
                  </FontAwesomeIcon>
                </DropdownToggle>
              )}
              <DropdownMenu compact={compact} width={width} isOpen={isOpen} {...getMenuProps()}>
                {isOpen
                  ? getFilteredItems(inputValue).map((item, index) => (
                      <DropdownItem
                        data-testid={generateTestId(index, 'dropdown', 'selection', 'value')}
                        key={item.key ?? String(item.value)}
                        onClick={item.disabled ? null : () => selectItem(item)}
                        disabled={item.disabled}
                        title={itemToString(item)}
                      >
                        {item.tooltip ? (
                          <Tooltip
                            message={item.tooltip}
                            align={align}
                            offset={offset}
                            origin={origin}
                          >
                            {item.content ?? <span>{itemToString(item)}</span>}
                          </Tooltip>
                        ) : (
                          <>{item.content ?? <span>{itemToString(item)}</span>}</>
                        )}
                      </DropdownItem>
                    ))
                  : null}
              </DropdownMenu>
            </div>
            {!!error && (
              <Text
                data-testid={generateTestId('dropdown', 'error')}
                variant="body2"
                className={clsx(classes.error, 'error')}
              >
                {error}
              </Text>
            )}
          </div>
        )}
      </Downshift>
      {!items?.length && (
        <ErrorMessage className={classes.noOptions}>
          No {label ? label.replace('*', '') : 'options'} available
        </ErrorMessage>
      )}
    </>
  )
}
